import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, UGX } from "../lib/api";
import { PageHeader, KpiCard, SectionCard, StatusPill, EmptyState } from "../components/Common";
import { Users, ClipboardList, Bell, UserPlus, Search, Printer, MessageSquare, LineChart as LineChartIcon, ArrowRight, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function Reception() {
  const [stats, setStats] = useState({});
  const [queue, setQueue] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/queue").then((r) => setQueue(r.data)).catch(() => {});
  }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Reception Dashboard"
        subtitle="Register patients, send them to triage, and take payments. Clinical records are restricted under the Uganda Data Protection & Privacy Act (2019), enforced by the PDPO."
        actions={
          <>
            <button data-testid="quick-existing" onClick={() => navigate("/app/register?mode=existing")} className="px-4 h-10 rounded-lg border border-slate-300 hover:bg-slate-50 text-sm font-medium flex items-center gap-1.5"><Search className="w-4 h-4" /> Existing patient</button>
            <button data-testid="quick-register" onClick={() => navigate("/app/register?mode=new")} className="px-4 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"><UserPlus className="w-4 h-4" /> New patient</button>
          </>
        }
      />

      {/* Quick actions — moved to top for easy access */}
      <SectionCard testId="reception-quick" title="Quick actions">
        <div className="p-4 grid grid-cols-2 lg:grid-cols-6 gap-2">
          <button data-testid="quick-new-patient" onClick={() => navigate("/app/register?mode=new")} className="px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 flex items-center gap-2"><UserPlus className="w-3.5 h-3.5" /> Register new</button>
          <button data-testid="quick-existing-patient" onClick={() => navigate("/app/register?mode=existing")} className="px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 flex items-center gap-2"><Search className="w-3.5 h-3.5" /> Existing patient</button>
          <button data-testid="quick-triage" onClick={() => navigate("/app/queue")} className="px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 flex items-center gap-2"><ArrowRight className="w-3.5 h-3.5" /> Send to triage</button>
          <button data-testid="quick-payment" onClick={() => navigate("/app/billing")} className="px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 flex items-center gap-2"><CreditCard className="w-3.5 h-3.5" /> Take payment</button>
          <button data-testid="quick-print-card" onClick={() => toast.info("Print a patient's QR card from the registration screen")} className="px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 flex items-center gap-2"><Printer className="w-3.5 h-3.5" /> Print card</button>
          <button data-testid="quick-sms" onClick={() => toast.success("SMS reminders sent to 3 patients (DEMO)")} className="px-3 py-2.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 flex items-center gap-2"><MessageSquare className="w-3.5 h-3.5" /> Send SMS</button>
        </div>
      </SectionCard>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-patients-today" label="Registered today" value={stats.patients_today || 0} icon={UserPlus} tone="blue" hint="Includes walk-ins" />
        <KpiCard testId="kpi-queue" label="Waiting queue" value={stats.queue_waiting || 0} icon={ClipboardList} tone="amber" hint="Live status" />
        <KpiCard testId="kpi-patients-total" label="Total patients" value={(stats.patients_total || 0).toLocaleString()} icon={Users} tone="slate" />
        <KpiCard testId="kpi-outstanding" label="Outstanding bills" value={UGX(stats.outstanding)} icon={LineChartIcon} tone="red" hint="Across all patients" />
      </div>

      <SectionCard
        testId="waiting-room"
        title="Waiting room"
        actions={<span className="text-xs text-slate-500">Today · {queue.length} in queue</span>}
      >
        {queue.length === 0 ? (
          <EmptyState icon={ClipboardList} title="No patients in the queue" description="Register or check in a patient to add them here." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Q#</th>
                  <th className="text-left px-4 py-2.5">Patient</th>
                  <th className="text-left px-4 py-2.5">Patient ID</th>
                  <th className="text-left px-4 py-2.5">Visit</th>
                  <th className="text-left px-4 py-2.5">Priority</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {queue.map((q) => (
                  <tr key={q.queue_id} data-testid={`queue-row-${q.queue_id}`}>
                    <td className="px-4 py-3 font-semibold tabular">#{q.queue_no}</td>
                    <td className="px-4 py-3">{q.patient_name}</td>
                    <td className="px-4 py-3 tabular text-slate-600">{q.patient_ths_id}</td>
                    <td className="px-4 py-3">{q.visit_type} · {q.department}</td>
                    <td className="px-4 py-3"><StatusPill status={q.priority === "Urgent" ? "critical" : "normal"} /></td>
                    <td className="px-4 py-3"><StatusPill status={q.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
